import { useQuery } from '@tanstack/react-query'
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  ResponsiveContainer,
} from 'recharts'

import {
  type Kpi as TileKpi,
  KpiRow,
  spanDays,
  Pagination,
  Thumb,
  metricCell,
  numberStyle,
} from '../components/AssetBits'
import { formatCount, TipCard } from '../components/ChartTip'
import { InfoDot } from '../components/InfoDot'
import { ManualMatchControl } from '../components/ManualMatch'
import { StoryDailyPanel } from '../components/StoryDailyPanel'
import { SelectionBar, postOpener, selectColumn } from '../components/SendToRawData'
import { TableToolbar } from '../components/TableToolbar'
import { Card, Note, Skeleton, Tag } from '../components/ui'
import { api, type Kpi, type SpotlightRow } from '../lib/api'
import {
  type LanguageGroup,
  PERIOD_LABELS,
  type Period,
  precedingRange,
  type Scope,
  useFilters,
} from '../lib/filters'
import { NO_ROWS, searchOnly } from '../lib/tableData'
import { ViewHeader } from '../shell/ViewHeader'
import { formatDate, formatRange } from '../lib/dates'
import { formatDuration } from '../lib/numbers'

/**
 * **Spotlights** — `APPLICATION_SPEC.md` §4.3.
 *
 * Spotlights are the inverse of stories in what Snap gives us: titles (~99%),
 * duration (677/678) and per-asset screenshots (650/680) all work here and none
 * work on stories. And one field works *only* here — `SPOTLIGHT_SUBSCRIBES`
 * reports on 536 of 549 assets where `STORY_SUBSCRIBES` is 0 of 307 — so this
 * is the only page that can answer *what acquires subscribers*, as against what
 * merely gets viewed.
 *
 * Two decisions are visible on this page:
 * · **Q-C12** — one superset of columns on both language groups, rather than
 *   the spec's two different column sets for the same content type.
 * · **Q-I11** — "Lifetime" here means **Snap's real lifetime** (roughly 1–2.5
 *   years, assets back to 2023-08-02), labelled as such because it is not
 *   comparable to any other page's window.
 *
 * ### The four contracted tables
 *
 * | Spec table | Group | Window |
 * |---|---|---|
 * | 8 · English Spotlights, this year | English | *This year* |
 * | 9 · English Spotlights, lifetime | English | Snap lifetime |
 * | 10 · International Spotlights, this year | International | *This year* |
 * | 11 · International Spotlights, lifetime | International | Snap lifetime |
 *
 * *This Year* is the **This year** chip on the global range control (Q-I10), and
 * the language groups are the segmented control below the tabs — the endpoint
 * has accepted `group` since it was written.
 */

type Window = 'range' | 'lifetime'

/** The Spotlights search box: "Search Channel or Title" — see lib/tableData. */
const SEARCHABLE = searchOnly(['channel', 'title'])

const COLUMNS = [
  'SPOTLIGHT_VIEWS',
  'SPOTLIGHT_UNIQUES',
  'SHARES',
  'REPLIES',
  'SPOTLIGHT_SCREENSHOTS',
  'SPOTLIGHT_SUBSCRIBES',
] as const

const COLUMN_LABELS: Record<string, string> = {
  SPOTLIGHT_VIEWS: 'Views',
  SPOTLIGHT_UNIQUES: 'Viewers',
  SHARES: 'Shares',
  REPLIES: 'Replies',
  SPOTLIGHT_SCREENSHOTS: 'Screenshots',
  SPOTLIGHT_SUBSCRIBES: 'Subscribers',
}

const RUN_TIME_NOTE = "From Snapchat's Duration Field. On Stories, Run Time Comes from ClickUp Instead."

const NOTES: Record<string, string> = {
  SPOTLIGHT_VIEWS: 'Times a Spotlight Was Viewed.',
  SPOTLIGHT_SUBSCRIBES: 'New Subscribers from This Spotlight.',
  SPOTLIGHT_UNIQUES: 'People, Not Views. Snap Lifetime Only.',
  AVG_DAILY_VIEWERS:
    "On a Typical Day, How Many People Watched a Spotlight. Each Channel's Daily Unique Spotlight Viewers, Averaged Over the Range and Added Across Channels, So Someone Who Watched Two Channels That Day Counts Twice. Not the Number of Different People Over the Whole Range: Snapchat Does Not Report That.",
}

/**
 * Tile notes over a date range, where a tile's source is not the table's.
 * **Wording agreed with Nilay, 8 Oct** — "not comparable", never "unreliable":
 * each listed spotlight's figures are right; it is the list that is short.
 */
const LISTED_NOTE =
  "Not Comparable Between Periods: Snapchat's List of Spotlights Is Incomplete, and Leaves Out a Different Number Each Week."
const CHANNEL_NOTE =
  "Snapchat's Total for All Your Spotlights, So It Can Be Compared with the Previous Period. May Be Higher Than the Table, Which Only Shows the Spotlights Snapchat Lists."
const RANGE_TILE_NOTES: Record<string, string> = {
  SPOTLIGHT_VIEWS: CHANNEL_NOTE,
  SPOTLIGHT_SUBSCRIBES: CHANNEL_NOTE,
  SHARES: LISTED_NOTE,
  REPLIES: LISTED_NOTE,
  SPOTLIGHT_SCREENSHOTS: LISTED_NOTE,
}

/**
 * The KPI row's tiles, with a change against the preceding window **only where
 * the tile is Snap's channel total.**
 *
 * Snap's spotlight list leaves spotlights out, by a different amount each week
 * — the listed sum was 43–83% of the channel figure across four weeks measured
 * 8 Oct — so two periods of a listed sum compare the list, not the content.
 * Views, Avg Daily Viewers and Subscribers Gained have a spotlight-only channel
 * figure and are compared; Shares, Replies and Screenshots have none.
 *
 * Both windows must hold the same number of days, as on Stories and Channel
 * Overview: history starts 20 Jul, so a prior window can be the right length
 * and still short.
 */
export function spotlightTiles(
  now: Kpi[],
  before: Kpi[] | undefined,
  previous: { from: string; to: string },
): TileKpi[] {
  const prior = new Map((before ?? []).map((kpi) => [kpi.metric, kpi]))
  const deltaLabel = `Against ${formatRange(previous.from, previous.to)}, the Preceding Window of Equal Length`
  return now.map((kpi) => {
    const tile: TileKpi =
      kpi.metric === 'AVG_DAILY_VIEWERS'
        ? { ...kpi, label: 'Avg Daily Viewers', kind: 'avg' }
        : { ...kpi }
    const then = prior.get(kpi.metric)
    if (
      kpi.basis !== 'channel' ||
      kpi.value === null ||
      !then ||
      then.basis !== 'channel' ||
      then.value === null ||
      then.value === 0 ||
      !kpi.days ||
      then.days !== kpi.days
    ) {
      return tile
    }
    return {
      ...tile,
      delta: (kpi.value - then.value) / then.value,
      deltaLabel,
      previous: then.value,
    }
  })
}

/** Contracted on all four spotlight tables, and with no source today. */
/** 🔴 **This used to read "Not available for spotlights." — a claim about Snap,
 *  when it was a fact about our own `WHERE` clause.** The matcher was scoped to
 *  public stories by an inference that did not hold; CU-3 widened it, and 402 of
 *  708 spotlights now carry episode details. What remains true is narrower and
 *  is the client's own process: ClickUp files spotlight tasks for the three
 *  English channels only. */
const ABSENT_CLICKUP =
  'From ClickUp, Which Tracks Spotlights for the Three English Channels Only.'

const ORDINAL = ['var(--seq-200)', 'var(--seq-300)', 'var(--seq-400)', 'var(--seq-500)', 'var(--seq-600)']

/**
 * The scope this page shows when the URL carries none.
 *
 * **English by default** — the client spec's own first four tables are English,
 * and three channels is a readable table where thirteen is a scroll. `all` is
 * one click away and, once chosen, travels in the URL.
 */
const DEFAULT_SCOPE = 'english' as const

/** `all` is not a value the API takes: it means "do not filter by group". */
function groupParam(scope: Scope): LanguageGroup | undefined {
  return scope === 'all' ? undefined : scope
}

export function Spotlights() {
  const { from, to, channels, group, period } = useFilters()

  // One dataset, so no dataset switch: the window that used to be a tab row is
  // the period control in the header, where every other window on the app now
  // lives. Snap lifetime is offered here and means something different from
  // everywhere else (Q-I11).
  const window: Window = period === 'lifetime' ? 'lifetime' : 'range'
  const scope: Scope = group ?? DEFAULT_SCOPE
  const query = { from, to, window, channels, group: groupParam(scope) }
  const key = [window, from, to, channels.join(','), scope]
  const assets = useQuery({
    queryKey: ['spotlight-assets', ...key],
    queryFn: () => api.spotlightAssets(query),
  })
  const kpis = useQuery({
    queryKey: ['spotlight-kpis', ...key],
    queryFn: () => api.spotlightKpis(query),
  })
  // The same tiles over the preceding window of equal length — compared only
  // where the tile is Snap's channel total (see `spotlightTiles`).
  const previous = precedingRange(from, to)
  const kpisBefore = useQuery({
    queryKey: ['spotlight-kpis', 'range', previous.from, previous.to, channels.join(','), scope],
    queryFn: () => api.spotlightKpis({ ...query, from: previous.from, to: previous.to }),
    enabled: window === 'range',
  })
  const bands = useQuery({
    queryKey: ['spotlight-bands', ...key],
    queryFn: () => api.durationBands(query),
  })

  const rows = assets.data?.rows ?? NO_ROWS
  const withheld = assets.data?.withheld ?? []

  return (
    <div>
      <ViewHeader
        title="Spotlights"
        status={
          window === 'lifetime'
            ? 'Snap’s Own Totals, Since Each Spotlight Was First Published'
            : 'Snap’s Day Is UTC'
        }
        defaultScope={DEFAULT_SCOPE}
        allowLifetime
        // Stories carries a dataset tab row and this page does not, so its KPI
        // row would sit 50px higher without that height reserved (X-12).
        reserveTabRow
      />

      {kpis.data && (
        <KpiRow
          // The API sends Avg Daily Viewers in Viewers' place over a range; its
          // label and "Avg" foot are the page's to say, as on Stories.
          kpis={spotlightTiles(kpis.data, kpisBefore.data, previous)}
          notes={window === 'lifetime' ? NOTES : { ...NOTES, ...RANGE_TILE_NOTES }}
          span={window === 'lifetime' ? 'lifetime' : { days: spanDays(from, to) }}
        />
      )}

      <div className="mb-4 grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(26rem, 100%), 1fr))' }}>
        <DurationBands data={bands.data ?? []} loading={bands.isLoading} />
        <AcquisitionScatter rows={rows} loading={assets.isLoading} />
      </div>

      <SpotlightTable
        rows={rows}
        withheld={withheld}
        loading={assets.isLoading}
        error={assets.error as Error | null}
        period={period}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------

function DurationBands({
  data,
  loading,
}: {
  data: { band: string; spotlights: number; median_views: number }[]
  loading: boolean
}) {
  return (
    <Card
      title="Median Views by Run Time Band"
    >
      {loading ? (
        <Skeleton rows={4} />
      ) : (
        <div style={{ height: '13rem' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="var(--grid-line)" vertical={false} />
              <XAxis
                dataKey="band"
                tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
                stroke="var(--axis-line)"
              />
              <YAxis
                tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
                stroke="var(--axis-line)"
                width={52}
                tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
              />
              <Tooltip
                cursor={{ fill: 'var(--brand-tint)' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null
                  const band = payload[0]!.payload as {
                    band: string
                    spotlights: number
                    median_views: number
                  }
                  return (
                    <TipCard
                      title={`${band.band} spotlights`}
                      rows={[
                        { label: 'Median Views', value: formatCount(band.median_views) },
                        { label: 'Spotlights in Band', value: formatCount(band.spotlights) },
                      ]}
                    />
                  )
                }}
              />
              {/* Ordered categories, so the ordinal ramp — never a categorical
                  palette, which would imply the bands are unrelated kinds. */}
              <Bar dataKey="median_views" isAnimationActive={false}>
                {data.map((entry, index) => (
                  <Cell key={entry.band} fill={ORDINAL[index % ORDINAL.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  )
}

function AcquisitionScatter({ rows, loading }: { rows: SpotlightRow[]; loading: boolean }) {
  const points = rows
    .filter(
      (row) =>
        row.metrics.SPOTLIGHT_VIEWS != null && row.metrics.SPOTLIGHT_SUBSCRIBES != null,
    )
    .map((row) => ({
      x: row.metrics.SPOTLIGHT_VIEWS as number,
      y: row.metrics.SPOTLIGHT_SUBSCRIBES as number,
      title: row.title,
      channel: row.display_label,
      published: formatDate(row.published_at),
      duration: row.duration,
    }))

  return (
    <Card
      title="Views × Subscribers Gained"
      info="Each Dot Is One Spotlight: Views Across, New Subscribers Up."
    >
      {loading ? (
        <Skeleton rows={4} />
      ) : points.length === 0 ? (
        <Note>No Spotlights with Both Figures in This Selection.</Note>
      ) : (
        <div style={{ height: '13rem' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="var(--grid-line)" />
              <XAxis
                type="number"
                dataKey="x"
                name="Views"
                tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
                stroke="var(--axis-line)"
                tickFormatter={(v: number) =>
                  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : `${Math.round(v / 1000)}k`
                }
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Subscribers"
                tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
                stroke="var(--axis-line)"
                width={48}
              />
              <ZAxis range={[24, 24]} />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null
                  const point = payload[0]!.payload as (typeof points)[number]
                  return (
                    <TipCard
                      title={point.title || `${point.channel}, untitled`}
                      subtitle={[
                        point.channel,
                        point.published,
                        point.duration === null ? null : formatDuration(point.duration),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      rows={[
                        { label: 'Spotlight Views', value: formatCount(point.x) },
                        { label: 'Subscribers Gained', value: formatCount(point.y) },
                      ]}
                    />
                  )
                }}
              />
              {/* Single hue: colour-by-channel is legal only on English's three
                  channels, and this view spans whatever the filter selects. The
                  transparent stroke widens the hit target — see the Stories
                  scatter. */}
              <Scatter
                data={points}
                fill="var(--data-1)"
                fillOpacity={0.55}
                stroke="transparent"
                strokeWidth={8}
                isAnimationActive={false}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  )
}

function SpotlightTable({
  rows,
  withheld,
  loading,
  error,
  period,
}: {
  rows: SpotlightRow[]
  withheld: string[]
  loading: boolean
  error: Error | null
  period: Period
}) {
  // Newest first, per Nilay 10 Sep (SP-3). A views-sorted table answers "what
  // did best ever"; the question a reader arrives with is "what went out lately".
  const [sorting, setSorting] = useState<SortingState>([{ id: 'published', desc: true }])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [search, setSearch] = useState('')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  // The row whose views-per-day panel is open.
  const [daily, setDaily] = useState<SpotlightRow | null>(null)
  // RD-21: Snap Lifetime shows lifetime totals, so a click opens those; every
  // other period sums daily rows, so a click opens the days.
  const opener = useMemo(
    () => postOpener<SpotlightRow>('SPOTLIGHT', period === 'lifetime' ? 'lifetime' : 'range'),
    [period],
  )

  const columns = useMemo<ColumnDef<SpotlightRow>[]>(() => {
    // A metric the window cannot answer is not a column. Over a date range
    // `SPOTLIGHT_UNIQUES` is deduplicated, so there is no honest figure to show
    // — the note under the title says where to find one.
    const metricColumns = COLUMNS.filter((metric) => !withheld.includes(metric)).map((metric) => ({
      id: metric,
      header: () => (
        <>
          {COLUMN_LABELS[metric]}
          {(NOTES[metric] || withheld.includes(metric)) && (
            <InfoDot
              note={
                withheld.includes(metric)
                  ? `Not Derivable over a Date Range. ${COLUMN_LABELS[metric]} Counts People, Not Views, so Adding the Days Would Count the Same Person Twice. Lifetime Has Snap's Own Figure.`
                  : NOTES[metric]!
              }
            />
          )}
        </>
      ),
      accessorFn: (row: SpotlightRow) => row.metrics[metric] ?? null,
      cell: (info: { getValue: () => unknown; row: { original: SpotlightRow } }) => {
        const value = info.getValue() as number | null
        // The Stories panel, on Spotlights (Nilay, 7 Oct): the Views figure
        // opens this spotlight's views and viewers per day.
        if (metric !== 'SPOTLIGHT_VIEWS' || value === null || value === undefined) {
          return metricCell(value)
        }
        return (
          <button
            type="button"
            className="views-open"
            title="Views per Day"
            aria-label={`Views per Day for ${info.row.original.title || `${info.row.original.display_label} spotlight`}`}
            onClick={() => setDaily(info.row.original)}
          >
            {metricCell(value)}
          </button>
        )
      },
      meta: { numeric: true },
    })) as ColumnDef<SpotlightRow>[]

    return [
      selectColumn(opener),
      {
        id: 'thumb',
        header: '',
        cell: ({ row }) => (
          <Thumb
            assetId={row.original.asset_id}
            has={row.original.has_thumbnail}
            alt={row.original.title || 'Spotlight thumbnail'}
          />
        ),
      },
      { id: 'channel', header: 'Channel', accessorKey: 'display_label' },
      {
        id: 'title',
        header: 'Full Title',
        accessorKey: 'title',
        cell: (info) => (
          <>
            {(info.getValue() as string) || <span style={{ color: 'var(--text-label)' }}>—</span>}
            {/* MT-17: the ClickUp task behind Author and Episode #, set by hand. */}
            <ManualMatchControl target={{ ...info.row.original, asset_type: 'SPOTLIGHT' }} />
          </>
        ),
      },
      {
        id: 'published',
        header: 'Publish Date',
        accessorKey: 'published_at',
        cell: (info) => formatDate(info.getValue() as string | null),
      },
      {
        // SP-6 (Nilay, 25 Sep): Snap's `duration`, labelled Run Time so it
        // reads alongside Stories — where Run Time comes from ClickUp instead,
        // and the tooltip says which source each page uses. No Air Time here:
        // Snap gives a spotlight no end time.
        id: 'duration',
        header: () => (
          <>
            Run Time
            <InfoDot note={RUN_TIME_NOTE} />
          </>
        ),
        accessorKey: 'duration',
        cell: (info) => {
          // HR-5: a bare "48" under a column called Run Time reads as whichever
          // unit the reader assumes, and the plausible guesses are 60x apart.
          return formatDuration(info.getValue() as number | null)
        },
        meta: { numeric: true },
      },
      // Contracted on all four tables, and empty rather than omitted — the
      // client can account for every column in their doc.
      // Real columns since CU-3 — 402 of 708 spotlights now carry these. The
      // dot explains the empty ones: ClickUp files spotlight tasks for the
      // three English channels only.
      {
        id: 'author',
        header: () => (
          <>
            Author<InfoDot note={ABSENT_CLICKUP} />
          </>
        ),
        accessorKey: 'author',
        cell: (info: { getValue: () => unknown }) =>
          (info.getValue() as string) || <span style={{ color: 'var(--text-label)' }}>—</span>,
      } as ColumnDef<SpotlightRow>,
      {
        id: 'classification',
        header: () => (
          <>
            Classification<InfoDot note={ABSENT_CLICKUP} />
          </>
        ),
        accessorKey: 'classification',
        cell: (info: { getValue: () => unknown }) =>
          info.getValue() === 'rerun' ? <Tag tone="neutral">Re-Run</Tag> : (
            <span style={{ color: 'var(--text-label)' }}>—</span>
          ),
      } as ColumnDef<SpotlightRow>,
      ...metricColumns,
    ]
  }, [opener, withheld])

  const table = useReactTable({
    data: rows,
    columns,
    getColumnCanGlobalFilter: SEARCHABLE,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Bounded rendering — see Pagination's note. 50 rows is ~50 thumbnail
    // requests instead of 382.
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 50 } },
    // ST-21: selection survives paging and sorting because rows are keyed by
    // asset, not by position.
    getRowId: (row) => row.asset_id,
    enableRowSelection: true,
    state: { sorting, columnFilters, globalFilter: search, rowSelection },
    onRowSelectionChange: setRowSelection,
  })

  return (
    <Card
      // The card names the window, so a screenshot says which of the client's
      // four contracted tables it is.
      title={`Spotlights: ${PERIOD_LABELS[period].toLowerCase()}`}
      action={
        <span className="text-xs" style={{ color: 'var(--text-label)' }}>
          {loading ? '' : `${table.getFilteredRowModel().rows.length} spotlights`}
        </span>
      }
    >
      {loading ? (
        <Skeleton rows={5} />
      ) : error ? (
        <Note tone="error">{error.message}</Note>
      ) : rows.length === 0 ? (
        <Note>No Spotlights for This Selection.</Note>
      ) : (
        <>
          <StoryDailyPanel row={daily} assetType="SPOTLIGHT" onClose={() => setDaily(null)} />
          <SelectionBar table={table} opener={opener} />
          <TableToolbar
            table={table}
            placeholder="Search Channel or Title"
            facets={[{ id: 'channel', label: 'Channel' }]}
          />
          {/* No inner vertical scroll — see the note in Stories.tsx: a fixed
              box around hundreds of rows captures the wheel and reads as the
              page freezing. */}
          <div style={{ overflowX: 'auto' }}>
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id}>
                  {group.headers.map((header) => {
                    const numeric = (header.column.columnDef.meta as { numeric?: boolean })?.numeric
                    return (
                      <th
                        key={header.id}
                        aria-sort={
                          header.column.getIsSorted() === 'asc'
                            ? 'ascending'
                            : header.column.getIsSorted() === 'desc'
                              ? 'descending'
                              : 'none'
                        }
                        style={{
                          position: 'sticky',
                          top: 0,
                          background: 'var(--bg-surface)',
                          color: 'var(--text-label)',
                          borderBottom: '1px solid var(--border)',
                          padding: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="w-full px-2 py-2 text-xs font-semibold uppercase"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'inherit',
                            font: 'inherit',
                            letterSpacing: '0.06em',
                            textAlign: numeric ? 'right' : 'left',
                            cursor: 'pointer',
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === 'asc' && ' ▲'}
                          {header.column.getIsSorted() === 'desc' && ' ▼'}
                        </button>
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const numeric = (cell.column.columnDef.meta as { numeric?: boolean })?.numeric
                    return (
                      <td
                        key={cell.id}
                        className="px-2 py-1"
                        style={{
                          borderBottom: '1px solid var(--border)',
                          color: 'var(--text-primary)',
                          textAlign: numeric ? 'right' : 'left',
                          maxWidth: cell.column.id === 'title' ? '18rem' : undefined,
                          overflow: cell.column.id === 'title' ? 'hidden' : undefined,
                          textOverflow: cell.column.id === 'title' ? 'ellipsis' : undefined,
                          whiteSpace: 'nowrap',
                          ...(numeric ? numberStyle : {}),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
      {!loading && rows.length > 0 && (
        <Pagination
          page={table.getState().pagination.pageIndex}
          pageCount={table.getPageCount()}
          rowCount={table.getFilteredRowModel().rows.length}
          onChange={(next) => table.setPageIndex(next)}
        />
      )}
    </Card>
  )
}
